/**
 * SAID-DIIS — Admin Portal shell (Phase 1: Home + Requests).
 *
 * Rendered from App.js only when `user.role === 'admin'`. Provides its
 * own top bar, side navigation and page routing. All other admin
 * screens (Inventory, Returns, Waitlist, Approval History, Users)
 * remain on the legacy dashboard via the "Legacy Console" link,
 * so we can validate Phase 1 in isolation without regressing anything.
 *
 * Every data fetch here hits an existing, unmodified backend endpoint.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LayoutGrid, ClipboardCheck, Package, Undo2, Clock, ScrollText, Users,
  Bell, LogOut, KeyRound, UserCog, ChevronDown,
} from 'lucide-react';
import Avatar from '../components/Avatar';
import './styles.css';
import { apiClient } from '../api/client';
import { endpoints } from '../api/endpoints';
import { Toast, shortRef, formatShortTime } from './ui';
import HomePage from './pages/Home';
import RequestsPage from './pages/Requests';
import InventoryPage from './pages/Inventory';
import OverduePage from './pages/Overdue';
import HistoryPage from './pages/History';
import ReturnsPage from './pages/Returns';
import WaitlistPage from './pages/Waitlist';
import UsersPage from './pages/Users';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_implant-inventory/artifacts/g4gfj0jd_SAID%20logo.png';

/* Which nav items are already handled by the NEW portal.
 * Anything else is still owned by the legacy console. */
const PHASE_PAGES = new Set(['home', 'requests', 'inventory', 'overdue', 'history', 'returns', 'waitlist', 'users']);

const NAV_ITEMS = [
  { id: 'home',      label: 'Overview',           icon: LayoutGrid },
  { id: 'requests',  label: 'Requests',           icon: ClipboardCheck },
  { id: 'overdue',   label: 'Overdue Watch',      icon: Clock },
  { id: 'history',   label: 'Approval History',   icon: ScrollText },
  { id: 'inventory', label: 'Inventory',          icon: Package },
  { id: 'returns',   label: 'Returns',            icon: Undo2 },
  { id: 'waitlist',  label: 'Waitlist',           icon: Clock },
  { id: 'users',     label: 'Users',              icon: Users },
];

export default function AdminPortal({ user, onChangePassword, onEditProfile, onLogout }) {
  const [page, setPage] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  // When set, RequestsPage should auto-open the approve modal for this order.
  // Cleared as soon as the modal opens so it never re-fires.
  const [initialApproveOrderId, setInitialApproveOrderId] = useState(null);
  const menuRef = useRef(null);
  const bellRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = useCallback((message, variant = 'ok') => {
    setToast({ message, variant });
    window.setTimeout(() => setToast(null), 4200);
  }, []);

  // ── Global data slices (shared across pages) ──
  const [pendingForMe, setPendingForMe] = useState([]);   // requests waiting on THIS admin
  const [allOrders, setAllOrders] = useState([]);         // for filters + counts
  const [lowStock, setLowStock] = useState([]);
  const [overdueReturnable, setOverdueReturnable] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  const loadPending = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.orders.pendingForMe);
      // Backend returns { count, orders: [...] } — unwrap the envelope.
      const list = Array.isArray(data) ? data : (data?.orders || []);
      setPendingForMe(list);
    } catch (e) { console.error('pending-for-me', e); }
  }, []);
  const loadOrders = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.orders.list);
      setAllOrders(Array.isArray(data) ? data : []);
    } catch (e) { console.error('orders', e); }
  }, []);
  const loadLowStock = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.components.lowStock);
      setLowStock(Array.isArray(data) ? data : []);
    } catch (e) { console.error('low-stock', e); }
  }, []);
  const loadOverdue = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.orders.overdueReturnableMine);
      setOverdueReturnable(Array.isArray(data) ? data : []);
    } catch (e) { console.error('overdue-returnable', e); }
  }, []);
  const loadStats = useCallback(async () => {
    try {
      const { data } = await apiClient.get(endpoints.dashboard.stats);
      setStats(data || {});
    } catch (e) { console.error('stats', e); }
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadPending(), loadOrders(), loadLowStock(), loadOverdue(), loadStats()]);
    setLoading(false);
  }, [loadPending, loadOrders, loadLowStock, loadOverdue, loadStats]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  // Silent 60s refresh of the operational queues.
  useEffect(() => {
    const t = window.setInterval(() => {
      loadPending(); loadOrders(); loadOverdue();
    }, 60000);
    return () => window.clearInterval(t);
  }, [loadPending, loadOrders, loadOverdue]);

  // Close user / bell menu on outside click
  useEffect(() => {
    const handler = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (bellOpen && bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen, bellOpen]);

  // ── Derived counts for indicators / nav badges ──
  const counts = useMemo(() => ({
    pending: pendingForMe.length,
    lowStock: lowStock.length,
    overdue: overdueReturnable.length,
    defaulters: (() => {
      const seen = new Set();
      (overdueReturnable || []).forEach((r) => r?.student_id && seen.add(r.student_id));
      return seen.size;
    })(),
  }), [pendingForMe, lowStock, overdueReturnable]);

  const initials = (user?.full_name || user?.username || '·')
    .split(' ').filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');

  const handleNav = (item) => {
    setPage(item.id);
  };

  const ctx = {
    user, loading,
    pendingForMe, allOrders, lowStock, overdueReturnable, stats, counts,
    showToast,
    goto: setPage,
    refresh: {
      all: refreshAll,
      pending: loadPending,
      orders: loadOrders,
      lowStock: loadLowStock,
      overdue: loadOverdue,
      stats: loadStats,
    },
  };

  return (
    <div className="admin-portal" data-testid="admin-portal">
      {/* ── TOP BAR ────────────────────────────────────────── */}
      <header className="ap-topbar">
        <div className="ap-topbar-brand">
          <img className="ap-topbar-logo" src={LOGO_URL} alt="SAID" />
          <div className="ap-topbar-wordmark">
            <strong>SAID <span style={{ fontFamily: 'var(--diis-font-sans)', fontWeight: 500, fontSize: 10, letterSpacing: '0.24em', color: 'var(--diis-ink-subtle)', marginLeft: 4, verticalAlign: 2 }}>— DIIS</span></strong>
            <span>Faculty Console</span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Notification bell */}
          <div style={{ position: 'relative' }} ref={bellRef}>
            <button
              type="button"
              className="ap-topbar-btn"
              onClick={() => setBellOpen((v) => !v)}
              aria-label="Pending requests"
              data-testid="ap-bell-btn"
            >
              <Bell size={16} />
              {counts.pending > 0 && (
                <span className="ap-topbar-bell-badge" data-testid="ap-bell-badge">{counts.pending}</span>
              )}
            </button>

            {bellOpen && (
              <div className="ap-bell-menu" role="menu" data-testid="ap-bell-menu">
                <div className="ap-bell-header">
                  <strong>Awaiting your approval</strong>
                  <span className="ap-subtle">{counts.pending} pending</span>
                </div>
                <div className="ap-bell-list">
                  {pendingForMe.length === 0 ? (
                    <div className="ap-bell-empty">No pending requests directed to you.</div>
                  ) : pendingForMe.slice(0, 8).map((o) => (
                    <button
                      key={o.id}
                      type="button"
                      className="ap-bell-item"
                      onClick={() => {
                        setBellOpen(false);
                        setInitialApproveOrderId(o.id);
                        setPage('requests');
                      }}
                      data-testid={`ap-bell-item-${o.id}`}
                    >
                      <strong>{o.student_name || 'Student'} · {shortRef(o.id, 'REQ')}</strong>
                      <small>
                        {(o.items || []).length} item{(o.items || []).length === 1 ? '' : 's'}
                        {'  ·  '}
                        {formatShortTime(o.created_at)}
                      </small>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* User */}
          <div className="ap-topbar-user" ref={menuRef}>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column' }}>
              <span className="ap-topbar-user-name">{user?.full_name || user?.username}</span>
              <span className="ap-topbar-user-role">Faculty</span>
            </div>
            <button
              type="button"
              className="ap-topbar-btn"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="ap-user-menu-btn"
              aria-label="Account menu"
            >
              <span className="ap-avatar" aria-hidden="true" style={{ padding: 0, overflow: 'hidden' }}>
                <Avatar user={user} size={28} testId="ap-header-avatar" />
              </span>
              <ChevronDown size={14} />
            </button>

            {menuOpen && (
              <div className="ap-user-menu" role="menu" data-testid="ap-user-menu">
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onEditProfile && onEditProfile(); }}
                  data-testid="ap-edit-profile-btn"
                ><UserCog size={15} /> Edit profile</button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onChangePassword && onChangePassword(); }}
                  data-testid="ap-change-password-btn"
                ><KeyRound size={15} /> Change password</button>
                <button
                  type="button"
                  onClick={() => { setMenuOpen(false); onLogout && onLogout(); }}
                  data-testid="ap-logout-btn"
                ><LogOut size={15} /> Sign out</button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── LAYOUT ─────────────────────────────────────────── */}
      <div className="ap-layout">
        <nav className="ap-sidenav" aria-label="Admin navigation">
          <div className="ap-nav-group-label">Faculty</div>
          {NAV_ITEMS.map((item) => {
            const active = page === item.id;
            let count;
            if (item.id === 'requests') count = counts.pending || undefined;
            if (item.id === 'overdue') count = counts.overdue || undefined;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleNav(item)}
                className={`ap-nav-item${active ? ' is-active' : ''}`}
                data-testid={`ap-nav-${item.id}`}
              >
                <item.icon size={17} strokeWidth={1.7} />
                <span>{item.label}</span>
                {count ? <span className="ap-nav-count">{count}</span> : null}
              </button>
            );
          })}
        </nav>

        <main className="ap-main">
          <div className="ap-page">
            {page === 'home'      && <HomePage      ctx={ctx} />}
            {page === 'requests'  && <RequestsPage  ctx={ctx} initialApproveOrderId={initialApproveOrderId} clearInitialApprove={() => setInitialApproveOrderId(null)} />}
            {page === 'overdue'   && <OverduePage   ctx={ctx} />}
            {page === 'history'   && <HistoryPage   ctx={ctx} />}
            {page === 'inventory' && <InventoryPage ctx={ctx} />}
            {page === 'returns'   && <ReturnsPage   ctx={ctx} />}
            {page === 'waitlist'  && <WaitlistPage  ctx={ctx} />}
            {page === 'users'     && <UsersPage     ctx={ctx} />}
            {/* Fallback — should never render because non-Phase items dispatch to legacy. */}
            {!PHASE_PAGES.has(page) && <HomePage ctx={ctx} />}
          </div>
        </main>
      </div>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
